import { makeStyles, tokens } from '@fluentui/react-components';

export const useRevisionsTabStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    height: '100%',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  countText: {
    color: tokens.colorNeutralForeground3,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginLeft: 'auto',
  },
  tableWrapper: {
    flex: '0 1 auto',
    maxHeight: '100%',
    overflow: 'auto',
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingBlockEnd: tokens.spacingVerticalS,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerCell: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':first-child': {
      paddingInlineStart: tokens.spacingHorizontalXL,
    },
    ':last-child': {
      paddingInlineEnd: tokens.spacingHorizontalXL,
    },
  },
  row: {
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke2}`,
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':nth-child(even):hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  activeRow: {
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}`,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}`,
    },
    ':nth-child(even)': {
      backgroundColor: tokens.colorBrandBackground2,
    },
    ':nth-child(even):hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  cell: {
    verticalAlign: 'middle',
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    ':first-child': {
      paddingInlineStart: tokens.spacingHorizontalXL,
    },
    ':last-child': {
      paddingInlineEnd: tokens.spacingHorizontalXL,
    },
  },
  nameCell: {
    minWidth: 0,
  },
  nameText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  createdText: {
    display: 'block',
    whiteSpace: 'nowrap',
  },
  badgeBase: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      backgroundColor: 'Canvas',
      color: 'CanvasText',
      borderTopColor: 'CanvasText',
      borderRightColor: 'CanvasText',
      borderBottomColor: 'CanvasText',
      borderLeftColor: 'CanvasText',
    },
  },
  activeBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
    borderTopColor: tokens.colorPaletteGreenBorder2,
    borderRightColor: tokens.colorPaletteGreenBorder2,
    borderBottomColor: tokens.colorPaletteGreenBorder2,
    borderLeftColor: tokens.colorPaletteGreenBorder2,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
      borderTopColor: 'CanvasText',
      borderRightColor: 'CanvasText',
      borderBottomColor: 'CanvasText',
      borderLeftColor: 'CanvasText',
    },
  },
  inactiveBadge: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    '@media (forced-colors: active)': {
      color: 'GrayText',
      borderTopColor: 'GrayText',
      borderRightColor: 'GrayText',
      borderBottomColor: 'GrayText',
      borderLeftColor: 'GrayText',
    },
  },
  healthBadgeHealthy: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
    borderTopColor: tokens.colorPaletteGreenBorder2,
    borderRightColor: tokens.colorPaletteGreenBorder2,
    borderBottomColor: tokens.colorPaletteGreenBorder2,
    borderLeftColor: tokens.colorPaletteGreenBorder2,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
      borderTopColor: 'CanvasText',
      borderRightColor: 'CanvasText',
      borderBottomColor: 'CanvasText',
      borderLeftColor: 'CanvasText',
    },
  },
  healthBadgeUnhealthy: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
    borderTopColor: tokens.colorPaletteRedBorder2,
    borderRightColor: tokens.colorPaletteRedBorder2,
    borderBottomColor: tokens.colorPaletteRedBorder2,
    borderLeftColor: tokens.colorPaletteRedBorder2,
    '@media (forced-colors: active)': {
      color: 'LinkText',
      borderTopColor: 'LinkText',
      borderRightColor: 'LinkText',
      borderBottomColor: 'LinkText',
      borderLeftColor: 'LinkText',
    },
  },
  healthBadgeNone: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    '@media (forced-colors: active)': {
      color: 'GrayText',
      borderTopColor: 'GrayText',
      borderRightColor: 'GrayText',
      borderBottomColor: 'GrayText',
      borderLeftColor: 'GrayText',
    },
  },
  trafficText: {
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
  },
  replicasText: {
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero900,
    color: tokens.colorNeutralForeground3,
  },
  loadingState: {},
  skeletonCell: {
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
  },
  skeletonItem: {
    height: '20px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonName: {
    width: '72%',
  },
  skeletonBadge: {
    width: '88px',
  },
  skeletonDate: {
    width: '68%',
  },
  skeletonVersion: {
    width: '48%',
  },
  skeletonTraffic: {
    width: '40%',
  },
  skeletonReplicas: {
    width: '44%',
  },
  skeletonHealth: {
    width: '64%',
  },
  errorBar: {
    maxWidth: '100%',
    overflow: 'hidden',
  },
  errorBody: {
    wordBreak: 'break-word',
  },
});
